import { Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { list } from 'aws-amplify/storage';
import MainScene from '../Scenes/MainScene';
import { BehaviorSubject } from 'rxjs';
import exifr from 'exifr';
import { LatLonToXYConverter } from 'src/classes/LatLonToXY';
import { environment } from 'environment';
import { fetchAuthSession } from 'aws-amplify/auth';
import ChunkedScene from 'src/classes/ChunkedScene';
import { ImageCompressUtil } from './image-compression-service.service';

export interface UserPhoto {
  filepath: string;
  webviewPath?: string;
}

const toastController: ToastController = new ToastController();

interface ModerationLabel{
  ParentName: string;
  Confidence: number;
  Name: string;
  TaxonomyLevel: number;
}

interface BasicResponse{
  message: string;
}

interface SuccessfulUploadResponse extends BasicResponse {
  s3Path: string;
}

const forbiddenLabels = [
  'Explicit',
  'Violence',
  'Visually Disturbing',
  'Drugs & Tobacco',
  'Rude Gestures',
  'Hate Symbols',
];

@Injectable({
  providedIn: 'root',
})
export class PhotoService {
  public static  photos$ = new BehaviorSubject<UserPhoto[]>([]);
  static currentImageNames: string[] = [];
  static publicImageNames: string[] | void[] = [];
  static observers: Array<any> = [];
  public static identityId: string;
  public static username: string;

  constructor(private http: HttpClient) {}

  public async pickPicture() {

    console.log("pickPicture called");
    const image: Photo = await Camera.getPhoto({
      quality: 90,
      allowEditing: true,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Photos,
    });

    // image.webPath will contain a path that can be set as an image src.
    // You can access the original file using image.path, which can be
    // passed to the Filesystem API to read the raw data of the image,
    // if desired (or pass resultType: CameraResultType.Base64 to getPhoto)
    console.log("imageSelected:");
    console.log(image);

    // Unfortunately exif is undefined for newly taken photos in
    // capacitor photo plugin. We will need to pull lat/lon/time manually
    const imageBlob = await PhotoService.getDataAsBlob(image);
    const {fileName, chunkId}  = await PhotoService.generateStandardizeImageName(imageBlob);
    console.log(`fileName: ${fileName}`);
    console.log(`chunkId: ${chunkId}`);

    // Resize the image if it is too big
    const resizedImage = await ImageCompressUtil.compressImage(image.dataUrl);
    
    if(fileName){
      try {

        // Get the user session to use for authentication.
        const session = await fetchAuthSession();
        console.log("session:");
        console.log(session);

        // Display toast informing user of AI moderation
        const toast1 = await toastController.create({
          message: 'Photo awaiting AI moderation.\nTakes about 5 seconds.',
          duration: 3000,
          color: 'primary',
          position: 'top',
        });
        await toast1.present();

        // user the /photos lambda api to upload the image
        const postBody = JSON.stringify({image_data: resizedImage, chunkId: chunkId});
        this.http.post(`${environment.REST_API}photos?filename=${fileName}`,
          postBody,
          {
            headers: new HttpHeaders({
              Authorization: `Bearer ${session.tokens.idToken}`
            }),
          }
        ).subscribe({
          next: async (data: SuccessfulUploadResponse) => {
              console.log("Photo submitted!");
              console.log(data.message);
              await PhotoService.getUploadedImage(data.s3Path);
          },
          error: async error => {
              toast1.dismiss();
              console.error('There was an error submitting your photo!', error);
              const toast = await toastController.create({
                message: error.error.message,
                duration: 2000,
                color: 'warning',
                position: 'top',
              });
              await toast.present();
          }
        });
      } catch (error) {
        console.error('Error : ', error);

        const toast = await toastController.create({
          message: 'Error uploading photo!',
          duration: 1500,
          color: 'warning',
          position: 'top',
        });
        await toast.present();
      }
    }
  }

  

  static async getUploadedImage(path: string){
    try{
      console.log("The image has been approved, retrieving it now.");
      const s3ImageSignedURL = await fetch(`${environment.REST_API}get-signed-url?bucket=st-images-bucket&key=${path}`);
      const imageSrc = (await s3ImageSignedURL.json()).signedUrl!.toString();
      const photos = PhotoService.photos$.getValue();
      photos.unshift({
        filepath: path,
        webviewPath: imageSrc,
      });
      PhotoService.photos$.next(photos);

      // Add the photo to the mainScene
      if (MainScene.currentInstance) {
        MainScene.currentInstance.placeImage(path, 32);
      }

      const toast = await toastController.create({
        message: 'Photo approved and is now public!',
        duration: 2000,
        color: 'success',
        position: 'top',
      });
      await toast.present();
    }
    catch(error){
      // Image not found in public directory. Check if it was rejected.
      console.error("Unable to retrieve the image.", error);
      const toast = await toastController.create({
        message: "Unable to check image status.",
        duration: 2000,
        color: 'danger',
        position: 'top',
      });
      await toast.present();
    }
  }

  static async getDataAsBlob(photo: Photo) {
    // Fetch the photo, read as a blob, then convert to base64 format
    const response = await fetch(photo.dataUrl!);
    return await response.blob();
  }

  static DMS2DD(degrees, minutes, seconds, direction) {
    var dd = degrees + (minutes/60) + (seconds/3600);
    if (direction == "S" || direction == "W") {
      dd = dd * -1;
    }
    return dd;
  }
  
  // extract the coordinates from 
  static async getImageCoords(imageBlob) {
    const exifData = await exifr.parse(imageBlob);
    // the image may not have coordinates
    if (exifData.GPSLatitude != null){

      // latitude in decimal
      var latDeg = exifData.GPSLatitude[0];
      var latMin = exifData.GPSLatitude[1];
      var latSec = exifData.GPSLatitude[2];
      var latDir = exifData.GPSLatitudeRef;
      var lat = PhotoService.DMS2DD(latDeg, latMin, latSec, latDir);

      // longitude in decimal
      var lngDeg = exifData.GPSLongitude[0];
      var lngMin = exifData.GPSLongitude[1];
      var lngSec = exifData.GPSLongitude[2];
      var lngDir = exifData.GPSLongitudeRef;
      var lng = PhotoService.DMS2DD(lngDeg, lngMin, lngSec, lngDir);

      return {lat: lat, lng: lng};
    }
  };

  static async generateStandardizeImageName(imageBlob: Blob): Promise<{fileName: string, chunkId: string}> {
    try{
      const {lat, lng} = await PhotoService.getImageCoords(imageBlob);
      const {x, y } = LatLonToXYConverter.latlngToScreenXY(lat, lng);
      const chunkId = ChunkedScene.computeChunkID(x, y);
      console.log(`chunkId: ${chunkId}`);
      console.log("Using images GPS coordinates.");
      return {fileName: `${x}_${y}_${Date.now()}.png`, chunkId: String(chunkId)};
    }
    catch{
      const toast = await toastController.create({
        message: "No GPS coordinates found in image.\nWould you like to place it at the players current position instead?",
        buttons: [
          {
            text: 'Yes',
            role: 'accept',
          },
          {
            text: 'No',
            role: 'decline',
          },
        ],
        color: 'warning',
        position: 'top',
      });
      await toast.present();

      const result = await toast.onDidDismiss();
      if(result.role === "accept"){
        const {x, y} = MainScene.playerMetadata.playerPosition;
        console.log("Using player's position.");
        const chunkId = ChunkedScene.computeChunkID(x, y);
        console.log(`chunkId: ${chunkId}`);
        return {fileName: `${x}_${y}_${Date.now()}.png`, chunkId: String(chunkId)};
      }
    }
    return null;

  }

  public static async getAllImageNames(username: string='') {
    try {
      // Use the /getImagesByUsername lambda api to get image metadata
      const resp = await fetch(`${environment.REST_API}getImagesByUsername?username=${username}`,
        {
          method: 'GET',
        }
      );
      
      if (!resp.ok) {
        throw new Error(`Failed to get images metadata: ${resp.status} ${resp.statusText}`);
      }

      const response = await resp.json();
      console.log('getAllImageNames result:', response);
      // render list items from response.items
      const publicImagesList = response.items;
      return publicImagesList
        ? publicImagesList.map((a) => {
            return a.s3Path;
          })
        : [];
    } catch (error) {
      console.error('Error ', error);
    }
  }

  static async syncImages() {
    PhotoService.publicImageNames = await PhotoService.getAllImageNames(PhotoService.username);
    let photos = PhotoService.photos$.getValue();
    await PhotoService.publicImageNames.forEach(async (imageName) => {
      if (!photos.some((photo) => photo.filepath === imageName)) {
        const s3ImageSignedURL = await fetch(`${environment.REST_API}get-signed-url?bucket=st-images-bucket&key=${imageName}`);
        const imageSrc = (await s3ImageSignedURL.json()).signedUrl!.toString();
        photos.unshift({
          filepath: imageName,
          webviewPath: imageSrc!,
        });
      }
    });
    PhotoService.photos$.next(photos);
  }

  static async getImagesByUser(username: string): Promise<Array<UserPhoto>>{
    const imageNames = await PhotoService.getAllImageNames(username);
    let photos = [];
    imageNames.forEach(async (imageName) => {
      const s3ImageSignedURL = await fetch(`${environment.REST_API}get-signed-url?bucket=st-images-bucket&key=${imageName}`);
      const imageSrc = (await s3ImageSignedURL.json()).signedUrl!.toString();
      photos.unshift({
        filepath: imageName,
        webviewPath: imageSrc!,
      });
    });
    return photos;
  }

  public async deleteImage(s3Path: string): Promise<boolean> {
    try {

      const session = await fetchAuthSession();

      // user the /photos lambda api to delete the image
      this.http.delete(`${environment.REST_API}photos?s3Path=${s3Path}`,
        {
          headers: new HttpHeaders({
            Authorization: `Bearer ${session.tokens.idToken}`
          }),
        }
      ).subscribe({
        next: async (data: BasicResponse) => {
          console.log("Photo deleted!");
          console.log(data.message);

            // Remove the image from the local image display
            function removeValue(photo: UserPhoto, index, arr) {
              // If the value at the current array index matches the specified value
              if (photo.filepath === s3Path) {
              // Removes the value from the original array
                  arr.splice(index, 1);
                  return true;
              }
              return false;
          }
          const photos = PhotoService.photos$.getValue();
          photos.filter(removeValue);
          PhotoService.photos$.next(photos);

          const toast = await toastController.create({
            message: 'Photo deleted!',
            duration: 2000,
            color: 'success',
            position: 'top',
          });
          await toast.present();
        },
        error: async error => {
          console.error('There was an error deleting your photo!', error);
          const toast = await toastController.create({
            message: error.error.message,
            duration: 2000,
            color: 'warning',
            position: 'top',
          });
          await toast.present();
        }
      });

      console.log("Image removed successfully!");

      return true;
    } catch (error) {
      console.error('Error ', error);
      return false;
    }
  }

  // Setup pub/sub for photo service
  static subscribe(fn: any) {
    PhotoService.observers.push(fn);
  }

  static async broadcast() {
    PhotoService.observers.forEach((subscriber) => subscriber());
  }
}
